import type * as schema from "zapatos/schema";
import { z } from "zod/v4";
import type { AmpWorkflowStatusCategory } from "../../ad-studio/board/constants";

export const MIGRATED_FOLDER_PRODUCTS = [
  "ad_studio",
  "lifecycle_studio",
] as const;
export type MigratedFolderProduct = (typeof MIGRATED_FOLDER_PRODUCTS)[number];

export const FILE_TYPES = [
  "ad",
  "email",
  "sms",
  "push",
  "canvas",
  "doc",
  "upload",
  "html",
  "slides",
] as const;
export const FILE_VISIBILITIES = ["private", "workspace"] as const;
export const FILE_SORTS = ["newest", "oldest", "updated"] as const;
export const fileTypeSchema = z.enum(FILE_TYPES);
export const fileVisibilitySchema = z.enum(FILE_VISIBILITIES);
export const generationStatusSchema = z.enum(["generating", "ready", "failed"]);
export const fileSortSchema = z.enum(FILE_SORTS);
export type FileType = z.infer<typeof fileTypeSchema>;
export type FileVisibility = z.infer<typeof fileVisibilitySchema>;
export type FileSort = z.infer<typeof fileSortSchema>;
export type GenerationStatus = z.infer<typeof generationStatusSchema>;

// Browsers type a PSD as image/*, but no <img> can draw one.
export function isImageUpload(upload: { filetype: string }) {
  return (
    upload.filetype.startsWith("image/") &&
    upload.filetype !== "image/vnd.adobe.photoshop"
  );
}

// A video/* type does not mean that the browser can decode the file.
export function isVideoUpload(upload: { filetype: string }) {
  return upload.filetype.startsWith("video/");
}

/** An upload that draws as image or video media, not as a file card. */
export function isMediaUpload(upload: { filetype: string }) {
  return isImageUpload(upload) || isVideoUpload(upload);
}

/** The file types whose comments a file agent answers. */
export const FILE_TYPES_WITH_AGENT = [
  "canvas",
  "ad",
  "doc",
  "email",
  "sms",
  "push",
  "upload",
  "html",
  "slides",
] as const satisfies readonly FileType[];

export type FileTypeWithAgent = (typeof FILE_TYPES_WITH_AGENT)[number];

export function fileTypeHasAgent(type: FileType): type is FileTypeWithAgent {
  return (FILE_TYPES_WITH_AGENT as readonly FileType[]).includes(type);
}

/** The file types a session of their own edits, which the file's agent delegates to. */
export const FILE_TYPES_WITH_EDITOR = [
  "ad",
  "email",
  "sms",
  "push",
  "html",
  "slides",
] as const satisfies readonly FileTypeWithAgent[];

export type FileTypeWithEditor = (typeof FILE_TYPES_WITH_EDITOR)[number];

export function fileTypeHasEditor(type: FileType): type is FileTypeWithEditor {
  return (FILE_TYPES_WITH_EDITOR as readonly FileType[]).includes(type);
}

/** The file types whose agent can run on the Pi harness, which edits each one itself instead of delegating to an editor. */
export const FILE_TYPES_WITH_PI_AGENT = [
  "canvas",
  "ad",
  "doc",
  "upload",
  "slides",
] as const satisfies readonly FileTypeWithAgent[];

export type FileTypeWithPiAgent = (typeof FILE_TYPES_WITH_PI_AGENT)[number];

export function fileTypeHasPiAgent(
  type: FileType,
): type is FileTypeWithPiAgent {
  return (FILE_TYPES_WITH_PI_AGENT as readonly FileType[]).includes(type);
}

export type FileRow = Omit<
  schema.amp_files.JSONSelectable,
  "type" | "visibility" | "generation_status"
> & {
  type: FileType;
  visibility: FileVisibility;
  generation_status: GenerationStatus;
};

/** Where a file is filed: in Drafts, or in a Library folder (null for the top level). */
export type FileLocation =
  | { type: "drafts" }
  | { type: "folder"; folderId: string | null };

export type ExistingFileContent =
  | { type: "ad"; id: string }
  | { type: "email"; id: string }
  | { type: "sms" | "push"; id: string };

export type WriteResult<T> =
  | { status: "committed"; value: T }
  | { status: "conflict"; current: T };

export class FileNotFoundError extends Error {
  constructor() {
    super("File not found");
  }
}

export class FileValidationError extends Error {}

export type FileActor = {
  workspaceId: number;
  userId: number | null;
  /** Hightouch staff, who alone can see and change private files. */
  isStaff: boolean;
  sessionId?: string;
  interactionId?: string;
  messageId?: string;
};

export type FileLinkedTask = {
  taskId: schema.amp_tasks.JSONSelectable["id"];
  title: schema.amp_tasks.JSONSelectable["title"];
  projectId: schema.amp_projects.JSONSelectable["id"];
  projectName: schema.amp_projects.JSONSelectable["title"];
  parentTaskId: schema.amp_tasks.JSONSelectable["parent_task_id"];
  statusId: schema.amp_tasks.JSONSelectable["status_id"];
  statusName: schema.amp_workflow_statuses.JSONSelectable["name"] | null;
  statusCategory: AmpWorkflowStatusCategory | null;
};
